import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable, DataTableRowToggleEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";

// Leaflet Imports
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import callToast, { handleChangeData, validateFormFields } from "../../../../../utils/helper";
import PageHeading from "../../../../../Components/PageHeading";
import Label from "../../../../../Components/Label";

// Fix Leaflet default icon routing issues in React
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Helper component to auto-zoom the map to fit both markers
function MapBoundsUpdater({ start, end }: { start: any, end: any }) {
    const map = useMap();
    useEffect(() => {
        // Use a slight timeout to ensure the map container has fully expanded its height in the DOM
        const timeout = setTimeout(() => {
            map.invalidateSize();
            if (start && end) {
                const bounds = L.latLngBounds([start.lat, start.lng], [end.lat, end.lng]);
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
            } else if (start) {
                map.setView([start.lat, start.lng], 13);
            } else if (end) {
                map.setView([end.lat, end.lng], 13);
            }
        }, 100);
        return () => clearTimeout(timeout);
    }, [start, end, map]);
    return null;
}

export interface RouteRecord {
    id: number | null;
    route_name: string;
    start_location_name: string;
    start_coords: { lat: number; lng: number } | null;
    end_location_name: string;
    end_coords: { lat: number; lng: number } | null;
    distance_km: number | null;
    estimated_time_mins: number | null;
    status: { id: number; name: string } | null;
}

const VehicleRoute = () => {
    const mainTitle = "Predefined Routes";
    const dhakaCenter = { lat: 23.8103, lng: 90.4125 };

    const statuses = [
        { id: 1, name: 'Active' },
        { id: 2, name: 'Inactive' }
    ];

    const initialState: RouteRecord = {
        id: null,
        route_name: '',
        start_location_name: '',
        start_coords: null,
        end_location_name: '',
        end_coords: null,
        distance_km: null,
        estimated_time_mins: null,
        status: statuses[0], // Active by default
    };

    const toast = useRef<Toast | null>(null);
    const [formData, setFormData] = useState<RouteRecord>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);
    const [tableDataList, setTableDataList] = useState<RouteRecord[]>([]);

    // State to track expanded rows in the table
    const [expandedRows, setExpandedRows] = useState<any | undefined>(undefined);

    const startMarkerRef = useRef<L.Marker>(null);
    const endMarkerRef = useRef<L.Marker>(null);

    // Load Mock Data
    useEffect(() => {
        const mockData: RouteRecord[] = [
            {
                id: 1,
                route_name: 'Mirpur to Motijheel (Standard)',
                start_location_name: 'Mirpur 10, Dhaka',
                start_coords: { lat: 23.8069, lng: 90.3687 },
                end_location_name: 'Motijheel C/A, Dhaka',
                end_coords: { lat: 23.7275, lng: 90.4186 },
                distance_km: 12.5,
                estimated_time_mins: 45,
                status: statuses[0]
            },
            {
                id: 2,
                route_name: 'Uttara to Banani (Corporate Drop)',
                start_location_name: 'Uttara Sector 7, Dhaka',
                start_coords: { lat: 23.8728, lng: 90.3989 },
                end_location_name: 'Banani, Dhaka',
                end_coords: { lat: 23.7940, lng: 90.4043 },
                distance_km: 9.2,
                estimated_time_mins: 30,
                status: statuses[0]
            },
            {
                id: 3,
                route_name: 'Airport Pickup Route',
                start_location_name: 'Hazrat Shahjalal Airport',
                start_coords: { lat: 23.8433, lng: 90.4049 },
                end_location_name: 'Pan Pacific Sonargaon',
                end_coords: { lat: 23.7503, lng: 90.3934 },
                distance_km: 15.0,
                estimated_time_mins: 60,
                status: statuses[1] // Inactive
            }
        ];
        setTableDataList(mockData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Form Submission
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            { field: 'route_name', label: 'Route Name' },
            { field: 'start_location_name', label: 'Start Location' },
            { field: 'end_location_name', label: 'End Location' },
            { field: 'distance_km', label: 'Distance' },
            { field: 'estimated_time_mins', label: 'Estimated Time' },
            { field: 'status', label: 'Status' }
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        setIsSubmitting(true);

        setTimeout(() => {
            if (updatedId) {
                setTableDataList(tableDataList.map(item => item.id === updatedId ? { ...formData, id: updatedId } : item));
                callToast(toast, true, `Route updated successfully`);
            } else {
                const newId = tableDataList.length > 0 ? Math.max(...tableDataList.map(t => t.id as number)) + 1 : 1;
                setTableDataList([{ ...formData, id: newId }, ...tableDataList]);
                callToast(toast, true, `Route created successfully`);
            }

            setFormData(initialState);
            setModal(false);
            setUpdatedId(null);
            setIsSubmitting(false);
        }, 500);
    };

    // Marker Drag Handlers
    const startDragHandlers = useMemo(() => ({
        dragend() {
            const marker = startMarkerRef.current;
            if (marker) {
                const { lat, lng } = marker.getLatLng();
                setFormData(prev => ({ ...prev, start_coords: { lat, lng } }));
            }
        },
    }), []);

    const endDragHandlers = useMemo(() => ({
        dragend() {
            const marker = endMarkerRef.current;
            if (marker) {
                const { lat, lng } = marker.getLatLng();
                setFormData(prev => ({ ...prev, end_coords: { lat, lng } }));
            }
        },
    }), []);

    // UI Helpers for Table
    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const statusBodyTemplate = (rowData: RouteRecord) => {
        const statusId = rowData.status?.id;
        return (
            <span className={'w-full'}>
                {statusId === 1 ? message("Active", "approved") : message("Inactive", "cancelled")}
            </span>
        );
    };

    const routePathBodyTemplate = (rowData: RouteRecord) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">
                    <i className="pi pi-circle-fill text-green-500 mr-2" style={{ fontSize: '0.5rem' }}></i>
                    {rowData.start_location_name}
                </span>
                <span className="text-500 text-xs flex align-items-center ml-2 border-left-1 border-400 pl-2 py-1">
                    {rowData.distance_km} km • ~{rowData.estimated_time_mins} mins
                </span>
                <span className="text-700 text-sm">
                    <i className="pi pi-map-marker text-red-500 mr-1"></i>
                    {rowData.end_location_name}
                </span>
            </div>
        );
    };

    const actionTemplate = (rowData: RouteRecord) => {
        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button
                    icon={'pi pi-pencil'}
                    className={'border-rounded w-2rem h-2rem'}
                    onClick={() => {
                        setModal(true);
                        setUpdatedId(rowData.id);
                        setFormData({ ...rowData });
                    }}
                    outlined
                />
            </div>
        );
    };

    // Row Expansion Template - Shows Map for Selected Row
    const rowExpansionTemplate = (data: RouteRecord) => {
        return (
            <div className="p-4 rounded-md shadow-sm surface-50 border-1 surface-border">
                <h6 className="text-lg font-semibold mb-3 text-gray-700 flex align-items-center gap-2">
                    <i className="pi pi-map text-primary"></i> Map Preview
                </h6>
                <div style={{ height: '300px', width: '100%' }} className="border-round overflow-hidden shadow-1">
                    <MapContainer
                        center={data.start_coords ? [data.start_coords.lat, data.start_coords.lng] : [dhakaCenter.lat, dhakaCenter.lng]}
                        zoom={12}
                        style={{ height: '100%', width: '100%', zIndex: 1 }}
                        scrollWheelZoom={false}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                        {data.start_coords && (
                            <Marker position={[data.start_coords.lat, data.start_coords.lng]}>
                                <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-green-600">
                                    {data.start_location_name || 'Start Point'}
                                </Tooltip>
                            </Marker>
                        )}

                        {data.end_coords && (
                            <Marker position={[data.end_coords.lat, data.end_coords.lng]}>
                                <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-red-600">
                                    {data.end_location_name || 'End Point'}
                                </Tooltip>
                            </Marker>
                        )}

                        {/* Adjust bounds for view-only map */}
                        <MapBoundsUpdater start={data.start_coords} end={data.end_coords} />
                    </MapContainer>
                </div>
            </div>
        );
    };

    // Helper to only allow expansion if coordinates exist
    const allowExpansion = (rowData: RouteRecord) => {
        return rowData.start_coords !== null || rowData.end_coords !== null;
    };

    return (
        <div className={"card"}>
            <Toast ref={toast} />
            <PageHeading
                title={mainTitle}
                isCreate={true}
                createFunction={() => {
                    // Provide default coordinates in Dhaka so the map renders nicely for new routes
                    setFormData({
                        ...initialState,
                        start_coords: { lat: 23.8103, lng: 90.4125 }, // Dhaka
                        end_coords: { lat: 23.7900, lng: 90.4000 }
                    });
                    setUpdatedId(null);
                    setModal(true);
                }}
                back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{ minWidth: '60rem' }}
                paginator
                rows={10}
                emptyMessage="No routes mapped."
                stripedRows
                expandedRows={expandedRows}
                onRowToggle={(e: DataTableRowToggleEvent) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
            >
                <Column expander={allowExpansion} style={{ width: '3rem' }} />
                <Column field="route_name" header="Route Name" style={{ width: '25%' }} className="font-bold" />
                <Column header="Path & Details" style={{ width: '35%' }} body={routePathBodyTemplate} />
                <Column field="status" header="Status" style={{ width: '15%' }} body={statusBodyTemplate} />
                <Column header={"Action"} bodyStyle={{ textAlign: 'center' }} body={actionTemplate} headerClassName={"flex justify-content-end"} />
            </DataTable>

            {/* CREATE / EDIT DIALOG */}
            <Dialog
                visible={modal}
                style={{ width: "60rem" }}
                headerClassName="text-800 font-semibold text-xl"
                header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                breakpoints={{ "960px": "75vw", "640px": "90vw" }}
                onHide={() => {
                    setUpdatedId(null);
                    setModal(false);
                    setFormData(initialState);
                }}
            >
                <div>
                    <form onSubmit={handleSubmit}>

                        {/* Basic Info */}
                        <div className="grid">
                            <div className="col-12 md:col-8">
                                <Label label={"Route Name"} required />
                                <InputText
                                    value={formData.route_name}
                                    onChange={(e) => handleChangeData('route_name', e.target.value, formData, setFormData)}
                                    placeholder={"e.g., Mirpur to Motijheel Office"}
                                    className={`w-full`}
                                />
                            </div>
                            <div className="col-12 md:col-4">
                                <Label label={"Status"} required />
                                <Dropdown
                                    options={statuses}
                                    optionLabel="name"
                                    placeholder="Select status"
                                    value={formData.status}
                                    onChange={(e) => handleChangeData('status', e.value, formData, setFormData)}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Metrics */}
                        <div className="grid mt-2">
                            <div className="col-12 md:col-6">
                                <Label label={"Distance (km)"} required />
                                <InputNumber
                                    value={formData.distance_km}
                                    onValueChange={(e) => handleChangeData('distance_km', e.value, formData, setFormData)}
                                    mode="decimal"
                                    minFractionDigits={1}
                                    maxFractionDigits={2}
                                    suffix=" km"
                                    placeholder="e.g., 12.5"
                                    className="w-full p-fluid"
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <Label label={"Estimated Time (mins)"} required />
                                <InputNumber
                                    value={formData.estimated_time_mins}
                                    onValueChange={(e) => handleChangeData('estimated_time_mins', e.value, formData, setFormData)}
                                    suffix=" mins"
                                    placeholder="e.g., 45"
                                    className="w-full p-fluid"
                                />
                            </div>
                        </div>

                        {/* Location Details & Map */}
                        <div className="grid mt-3 border-top-1 surface-border pt-3">
                            <div className="col-12">
                                <h6 className="text-lg font-semibold mb-3 text-gray-700">Route Map Coordinates</h6>
                                <div className="grid mb-3">
                                    <div className="col-12 md:col-6">
                                        <Label label={"Start Location Name"} required />
                                        <InputText
                                            value={formData.start_location_name}
                                            onChange={(e) => handleChangeData('start_location_name', e.target.value, formData, setFormData)}
                                            placeholder={"e.g., Mirpur 10"}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <Label label={"End Location Name"} required />
                                        <InputText
                                            value={formData.end_location_name}
                                            onChange={(e) => handleChangeData('end_location_name', e.target.value, formData, setFormData)}
                                            placeholder={"e.g., Motijheel C/A"}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 text-500 text-sm mt-1">
                                        <i className="pi pi-info-circle mr-1"></i>
                                        Drag the markers on the map below to pinpoint the start and end coordinates.
                                    </div>
                                </div>

                                {/* EMBEDDED MAP CONTAINER */}
                                <div className="border-round shadow-1 overflow-hidden" style={{ height: '350px', width: '100%', position: 'relative' }}>
                                    {modal && (
                                        <MapContainer
                                            center={[dhakaCenter.lat, dhakaCenter.lng]}
                                            zoom={12}
                                            style={{ height: '100%', width: '100%', zIndex: 1 }}
                                        >
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                                            {/* Start Marker */}
                                            {formData.start_coords && (
                                                <Marker
                                                    position={[formData.start_coords.lat, formData.start_coords.lng]}
                                                    draggable={true}
                                                    ref={startMarkerRef}
                                                    eventHandlers={startDragHandlers}
                                                >
                                                    <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-green-600">
                                                        Start Point
                                                    </Tooltip>
                                                </Marker>
                                            )}

                                            {/* End Marker */}
                                            {formData.end_coords && (
                                                <Marker
                                                    position={[formData.end_coords.lat, formData.end_coords.lng]}
                                                    draggable={true}
                                                    ref={endMarkerRef}
                                                    eventHandlers={endDragHandlers}
                                                >
                                                    <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-red-600">
                                                        End Point
                                                    </Tooltip>
                                                </Marker>
                                            )}

                                            {/* Auto Center/Zoom to fit markers */}
                                            <MapBoundsUpdater start={formData.start_coords} end={formData.end_coords} />
                                        </MapContainer>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-content-end gap-2 mt-4">
                            <Button type="button" label="Cancel" icon="pi pi-times" severity="secondary" onClick={() => setModal(false)} disabled={isSubmitting} />
                            <Button type="submit" icon="pi pi-check" label={updatedId ? 'Update Route' : "Save Route"} className="p-button-primary" loading={isSubmitting} />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

export default VehicleRoute;